/* **The movement's photograph, as one frame drawn at four sizes.**
 *
 * The table's 44 × 33 cell, the preview panel's 16:9 card, the theatre's similar card and — through
 * the same component — anywhere else on this screen a still appears. Three near-identical blocks
 * existed before the theatre landed and the theatre would have been the fourth, which is precisely
 * the near-duplicate the styling rules exist to stop.
 *
 * ── What is here, and what stays on the surface — `ghostButton`'s contract ───────────────────
 * Two classes on the element: this one for the TREATMENT — the fallback ground, the crop, the
 * centring of the glyph — and the surface's own for the GEOMETRY, declared there as ordinary
 * properties:
 *
 *   width          height          aspect-ratio          border-radius
 *
 * `border-radius` is geometry here because it follows the size: the table's 44px cell wears
 * `--radius-xs` and the panel's 366px card wears `--radius-mark`, and a single value would be wrong
 * at one of the two. The photograph inherits it rather than restating it, so the two can never
 * disagree.
 *
 * **Nothing may be declared on both sides.** A declaration living in a shared module and in a
 * surface's own is settled by chunk order, which differs between the dev server and the build —
 * this repo has paid for that once already. */

.frame {
  display: grid;
  flex: none;
  place-items: center;
  overflow: hidden;
  color: var(--text-3);
  background: var(--bg-sunken);
}

/* The photograph fills the frame. `object-fit` is `shared/ui/photoBox`'s, which every picture in
 * this app already wears.
 *
 * It does NOT restate the corner: the frame clips with `overflow: hidden`, which rounds the picture
 * to exactly the caller's radius with nothing to keep in step. `border-radius: inherit` would have
 * been the same outcome written twice, and stylelint refuses `inherit` there for that reason. */
.photo {
  display: block;
  width: 100%;
  height: 100%;
}

/* The drawing that stands in for a photograph the catalogue does not name — or names and the media
 * root does not hold. A PROPORTION rather than a size, so one rule covers 44px and 366px alike.
 *
 * `--ex-still-glyph` is the one hook a caller may resize it through, set on the same element as the
 * geometry class — never by redeclaring `width` here and there, which chunk order would settle. The
 * proportion that reads well shrinks as the frame grows: 45% on the table's 44px cell, 27% on the
 * preview panel's 366px card. */
.glyph {
  display: block;
  width: var(--ex-still-glyph, 45%);
  height: var(--ex-still-glyph, 45%);
}

/* The drawing with words under it, for a box big enough to say „Brak okładki" — the card hero's own
 * tile. Stretched over the frame's one cell rather than sized in percent: the frame centres its
 * child, and a percentage height on a centred grid item does not resolve, so the glyph fell back to
 * the SVG's own 150px. */
.fallback {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  align-items: center;
  justify-content: center;
  place-self: stretch;
  min-height: 0;
}

.fallback .glyph {
  flex: none;
  height: var(--ex-still-glyph, 45%);
  width: auto;
  aspect-ratio: 1;
}

.caption {
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-3);
}
